<script lang="ts" setup>
import { Search, Dropdown, LoadingSpinner, Modal } from "@/components";
import { useI18n } from "vue-i18n";
import { ref, computed, watch, PropType } from "vue";
import {
  useFindVeevaDocumentsQuery,
  ExternalDocumentFragment,
} from "@/graphql/operations";
import Document from "./Document.vue";
import { useDebounceFn, useInfiniteScroll } from "@vueuse/core";
import { token } from "@/init";
import { HTTP } from "@/util";
import { PMDoc } from "./PromomatsDocument";
import { captureException } from "@sentry/vue";

const { t } = useI18n();

const BASE_URL = import.meta.env.VUE_APP_BACKEND_URL || "/backend";

const props = defineProps({
  singleSelect: {
    type: Boolean,
    required: false,
    default: false,
  },
  selectedDocuments: {
    type: Array as PropType<PMDoc[]>,
    required: true,
  },
});

const emit = defineEmits(["update:selectedDocuments", "close", "next"]);

const sortItems = [
  {
    value: "name__v,ASC",
    title: t("promomatsIntegration.sort.nameAsc"),
  },
  {
    value: "name__v,DESC",
    title: t("promomatsIntegration.sort.nameDesc"),
  },
  {
    value: "version_creation_date__v,ASC",
    title: t("promomatsIntegration.sort.versionCreationAsc"),
  },
  {
    value: "version_creation_date__v,DESC",
    title: t("promomatsIntegration.sort.versionCreationDesc"),
  },
  {
    value: "version_modified_date__v,ASC",
    title: t("promomatsIntegration.sort.versionModificationAsc"),
  },
  {
    value: "version_modified_date__v,DESC",
    title: t("promomatsIntegration.sort.versionModificationDesc"),
  },
];

const currentSort = ref("version_creation_date__v,ASC");
const updateSort = (value: string) => {
  currentSort.value = value;
  fetch();
};

const documents = ref<Array<PMDoc>>([]);
const fetching = ref(true);
const searchPending = ref(false);
const loadingMore = ref(false);
const nextPage = ref<string | null>(null);
const error = ref<string>();
const loadMoreError = ref<string>();

// Every list reset bumps the generation; responses belonging to an older
// generation are dropped so an in-flight load-more cannot concat stale rows
// (or a stale cursor) into a freshly reset list.
let fetchGeneration = 0;

const fetch = async (next?: string) => {
  const generation = next ? fetchGeneration : ++fetchGeneration;

  // `fetching` drives the v-if chain and would unmount the whole table while
  // a further page loads, so follow-up fetches only toggle `loadingMore`.
  if (next) {
    loadingMore.value = true;
  } else {
    fetching.value = true;
    searchPending.value = false;
    loadingMore.value = false;
    documents.value = [];
    nextPage.value = null;
  }
  error.value = undefined;
  loadMoreError.value = undefined;

  try {
    const url = new URL(BASE_URL + "/veeva/documents", window.location.origin);

    if (next) {
      // The next_page path from Veeva encapsulates the full query including
      // search and sorting, so no other parameters are needed.
      url.searchParams.append("next", next);
    } else {
      if (currentSearch.value && currentSearch.value.length > 0) {
        url.searchParams.append("search", currentSearch.value);
      }

      const sortParams = currentSort.value.split(",");
      url.searchParams.append("sortBy", sortParams[0]);
      url.searchParams.append("sort", sortParams[1]);
    }

    const headers = new Map();
    headers.set("Authorization", `Bearer ${token.value}`);
    const response = await HTTP.get(url.toString(), headers);
    // The backend reports Veeva failures as JSON with a non-2xx status;
    // other failure modes (proxy errors, crashes) may not carry JSON at all.
    const data = await response.json().catch(() => ({}));

    if (!response.ok || data.responseStatus === "FAILURE") {
      throw new Error(data.errors?.[0]?.message ?? `HTTP ${response.status}`);
    }

    if (generation !== fetchGeneration) return; // stale response, drop it

    let docs = data.data;
    documents.value = documents.value.concat(docs);
    nextPage.value = data.responseDetails?.next_page ?? null;
  } catch (e) {
    if (generation !== fetchGeneration) return;
    // A failed follow-up page must not wipe the rows already on screen, so
    // it reports through loadMoreError instead of the v-if-driving `error`.
    // nextPage stays set, so scrolling again retries the page.
    const message = e instanceof Error ? e.message : "An error occurred";
    if (next) {
      loadMoreError.value = message;
    } else {
      error.value = message;
    }
    captureException(e);
  } finally {
    if (generation === fetchGeneration) {
      fetching.value = false;
      loadingMore.value = false;
    }
  }
};

const contentEl = ref<HTMLElement | null>(null);
useInfiniteScroll(contentEl, () => fetch(nextPage.value!), {
  distance: 200,
  canLoadMore: () => !!nextPage.value && !fetching.value && !loadingMore.value,
});

const currentSearch = ref("");
const updateSearch = (value: string) => {
  currentSearch.value = value;
};
const hasSearchTerm = computed(() => currentSearch.value.trim().length > 0);
const showListStatus = computed(() => fetching.value || searchPending.value);
const loadingMessage = computed(() =>
  hasSearchTerm.value
    ? t("promomatsIntegration.search.loading")
    : t("promomatsIntegration.loading"),
);

const fetchSearchResults = useDebounceFn(
  () => {
    fetch();
  },
  700,
  { maxWait: 5000 },
);

watch(currentSearch, () => {
  // The input changed, but the debounced fetch has not started yet. Hide stale
  // rows immediately and invalidate any in-flight first-page/load-more response
  // so it cannot repaint old results during the debounce window.
  fetchGeneration++;
  searchPending.value = true;
  documents.value = [];
  nextPage.value = null;
  error.value = undefined;
  loadMoreError.value = undefined;
  fetchSearchResults();
});

fetch();

const { data } = useFindVeevaDocumentsQuery({
  requestPolicy: "cache-and-network",
});

const importedDocuments = computed(() => {
  return (
    data.value?.documents?.nodes
      ?.filter((doc): doc is ExternalDocumentFragment => doc !== null)
      .reduce(
        (d, doc) => {
          d[doc.veevaDocumentMetadatum?.veevaDocumentId!] = doc;
          return d;
        },
        {} as Record<string, ExternalDocumentFragment>,
      ) || {}
  );
});

const selectedDocumentIds = computed(
  () => new Set(props.selectedDocuments.map((d) => d.id)),
);

const isSelected = (id: Number) => selectedDocumentIds.value.has(id);

const updateSelectedDocuments = (docId: Number, isSelected: boolean) => {
  const next = new Set(selectedDocumentIds.value);

  if (props.singleSelect) {
    next.clear();
    if (isSelected) {
      next.add(docId);
    }
  } else {
    isSelected ? next.add(docId) : next.delete(docId);
  }

  const newSelection = Array.from(next).map((docId: Number) =>
    documents.value.find((d) => d.id === docId),
  );

  emit("update:selectedDocuments", newSelection);
};
</script>

<template>
  <Modal
    :active="true"
    @close="emit('close')"
    class="huge"
    data-test="upload-modal"
  >
    <template v-slot:header>
      {{ $t("promomatsIntegration.title") }}
    </template>
    <template v-slot:body>
      <div class="inner">
        <div class="controls">
          <Search
            @change="updateSearch"
            :placeholder="$t('promomatsIntegration.search.placeholder')"
          />
          <div class="control">
            <label>{{ $t("promomatsIntegration.sort.label") }}:</label>
            <dropdown
              :items="sortItems"
              :value="currentSort"
              @change="updateSort"
              class="right"
            >
              <div class="link">
                {{ sortItems.find((i) => i.value === currentSort)?.title }}
              </div>
            </dropdown>
          </div>
        </div>

        <div
          v-if="showListStatus"
          class="search_status"
          aria-live="polite"
          data-test="promomats-search-loading"
        >
          <LoadingSpinner :color="'var(--classic-text, black)'" />
          <span>{{ loadingMessage }}</span>
        </div>
        <div v-else-if="error" class="search_status error" aria-live="polite">
          {{ $t("promomatsIntegration.errors.loadDocuments") }} {{ error }}
        </div>
        <div
          v-else-if="documents.length === 0"
          class="search_status"
          aria-live="polite"
          data-test="promomats-search-empty"
        >
          {{ $t("promomatsIntegration.search.empty") }}
        </div>
        <div v-else ref="contentEl" class="content">
          <table class="data_table">
            <thead>
              <tr>
                <th></th>
                <th></th>
                <th>{{ $t("promomatsIntegration.columns.status") }}</th>
                <th class="align_right">
                  {{ $t("promomatsIntegration.columns.expirationDate") }}
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <Document
                v-for="(doc, idx) in documents"
                :key="idx"
                :doc="doc"
                :workspaceDoc="importedDocuments[doc.id.toString()]"
                :selected="isSelected(doc.id)"
                @update:selected="(val) => updateSelectedDocuments(doc.id, val)"
              />
            </tbody>
          </table>
          <div v-if="loadingMore" class="loading_more" aria-live="polite">
            <LoadingSpinner :color="'var(--classic-text, black)'" />
            <span>{{ $t("promomatsIntegration.loadingMore") }}</span>
          </div>
          <div v-else-if="loadMoreError" class="loading_more error">
            {{ $t("promomatsIntegration.errors.loadDocuments") }}
            {{ loadMoreError }}
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="buttons">
        <button
          class="primary button"
          :class="{ disabled: selectedDocumentIds.size === 0 }"
          @click="emit('next')"
        >
          {{
            $t("promomatsIntegration.next", {
              numDocs: selectedDocumentIds.size,
            })
          }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.inner {
  height: 100%;
}

.content {
  height: 100%;
  overflow: scroll;
  box-sizing: border-box;
  padding-top: 0.5em;
}

.controls {
  display: flex;
  align-items: center;
}

.controls input {
  margin-left: auto;
  height: 16px;
  width: 16px;
  padding: 0.5em;
}

.controls .clickable {
  cursor: pointer;
}

.controls .clickable.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.controls .search {
  margin-right: 1em;
}

.controls .control {
  font-size: 0.9em;
  display: flex;
  width: 240px;
  padding-right: 2em;
  justify-content: flex-end;
}

.controls .control label {
  margin-right: 0.5em;
}

.controls .control .link {
  text-decoration: none;
  font-weight: 500;
}

table.data_table {
  box-shadow: none;
  table-layout: fixed;
}

table.data_table thead tr th:first-child {
  width: 94px;
}

table.data_table thead tr th:nth-child(2) {
  width: 50%;
}

table.data_table thead tr th:nth-child(3) {
  width: 70px;
}

table.data_table thead tr th.align_right {
  text-align: right;
}

table.data_table thead tr th:last-child {
  width: 40px;
}

.loading_more {
  padding: 0.5em;
  text-align: center;
}

.search_status,
.loading_more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75em;
  color: var(--classic-text-secondary, #555);
}

.search_status {
  min-height: 180px;
  padding: 1.5em;
  box-sizing: border-box;
  text-align: center;
}

.search_status :deep(.lds-spinner),
.loading_more :deep(.lds-spinner) {
  width: 24px;
  height: 24px;
}

.search_status :deep(.lds-spinner div),
.loading_more :deep(.lds-spinner div) {
  transform-origin: 12px 12px;
}

.search_status :deep(.lds-spinner div:after),
.loading_more :deep(.lds-spinner div:after) {
  top: 1px;
  left: 11px;
  width: 2px;
  height: 6px;
}

.error {
  color: var(--classic-error-text, #b00020);
}
</style>
